import { Button, Spinner, Card, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faChild } from '@fortawesome/free-solid-svg-icons';
import { SortableHeader } from '../../../ui/SortableHeader/SortableHeader';
import { IFamilyMemberChildDto } from '../../../../utils/constants/interfaces';
import { Helpers } from '../../../../utils/helpers';
import { useFamilyMemberChildren } from './useFamilyMemberChildren';
import styles from './FamilyMemberChildrenSection.module.css';

interface FamilyMemberChildrenSectionProps {
  familyMemberChildren: IFamilyMemberChildDto[];
  loadingChildren: boolean;
  onAddChild: () => void;
  onEditChild: (child: IFamilyMemberChildDto) => void;
}

export const FamilyMemberChildrenSection = ({
  familyMemberChildren,
  loadingChildren,
  onAddChild,
  onEditChild
}: FamilyMemberChildrenSectionProps) => {
  const manager = useFamilyMemberChildren({
    familyMemberChildren,
    loadingChildren
  });

  return (
    <Card className="mb-4">
      <Card.Header>
        <h5 className="mb-0">
          <FontAwesomeIcon icon={faChild} className="me-2" />
          Family Member&apos;s Children
        </h5>
      </Card.Header>
      <Card.Body>
        {loadingChildren ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <div className="mt-2">Loading children...</div>
          </div>
        ) : familyMemberChildren.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <FontAwesomeIcon icon={faChild} size="2x" className="mb-3" />
            <div>No children recorded yet</div>
            <div className="small">Add children using the button below</div>
          </div>
        ) : (
          <div className="table-responsive">
            <Table striped hover>
              <thead>
                <tr>
                  <SortableHeader
                    text="First Name"
                    sortField="firstName"
                    active={manager.sortField === 'firstName'}
                    sortDirection={manager.sortDirection}
                    onClick={manager.changeSort}
                  />
                  <SortableHeader
                    text="Middle Name"
                    sortField="middleName"
                    active={manager.sortField === 'middleName'}
                    sortDirection={manager.sortDirection}
                    onClick={manager.changeSort}
                  />
                  <SortableHeader
                    text="Last Name"
                    sortField="lastName"
                    active={manager.sortField === 'lastName'}
                    sortDirection={manager.sortDirection}
                    onClick={manager.changeSort}
                  />
                  <SortableHeader
                    text="Birth Date"
                    sortField="dateOfBirthUtc"
                    active={manager.sortField === 'dateOfBirthUtc'}
                    sortDirection={manager.sortDirection}
                    onClick={manager.changeSort}
                  />
                  <SortableHeader
                    text="Age"
                    sortField="dateOfBirthUtc"
                    active={false}
                    sortDirection={manager.sortDirection}
                    onClick={() => {}}
                  />
                  <SortableHeader
                    text="Added By"
                    sortField="createdBy"
                    active={manager.sortField === 'createdBy'}
                    sortDirection={manager.sortDirection}
                    onClick={manager.changeSort}
                  />
                  <SortableHeader
                    text="Date Added"
                    sortField="dateCreatedUtc"
                    active={manager.sortField === 'dateCreatedUtc'}
                    sortDirection={manager.sortDirection}
                    onClick={manager.changeSort}
                  />
                </tr>
              </thead>
              <tbody>
                {manager.sortedChildren.map((child) => {
                  const age = child.dateOfBirthUtc
                    ? Math.floor(
                        (new Date().getTime() - new Date(child.dateOfBirthUtc).getTime()) /
                          (365.25 * 24 * 60 * 60 * 1000)
                      )
                    : null;

                  return (
                    <tr
                      key={child.id}
                      onClick={() => onEditChild(child)}
                      className={styles.clickableRow}
                      style={{ cursor: 'pointer' }}
                      title="Click to edit this child">
                      <td>{child.firstName}</td>
                      <td>{child.middleName || '-'}</td>
                      <td>{child.lastName}</td>
                      <td>{Helpers.toShortDate(child.dateOfBirthUtc)}</td>
                      <td>{age !== null ? `${age} years` : '-'}</td>
                      <td>{child.createdBy}</td>
                      <td>{Helpers.toShortDate(child.dateCreatedUtc)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        <div className="mt-3">
          <Button variant="primary" size="sm" onClick={onAddChild}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Child
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};
